/**
 * Payment Checkout Page — two-tab layout: Subscribe (card) | One-time (redirect).
 * Receipt summary, promo code, referral balance deduction, provider selection.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Plus, Tag, Zap, Check,
  ChevronRight, ChevronDown, X, AlertCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import {
  useUserCards,
  useCalculatePayment,
  useSubscribeWithCard,
  useCreateCheckout,
  useValidatePromo,
} from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { CardAddSheet } from './card-add-sheet'
import { ProviderSelector } from './provider-selector'
import type { PaymentCalculation, UserCard } from '@turnifly/shared/api'

interface PaymentCheckoutPageProps {
  isOpen: boolean
  onClose: () => void
  plan: string
  period: string
  promoCode?: string
  onSuccess: () => void
}

type PaymentTab = 'subscribe' | 'one_time'

export const PaymentCheckoutPage: React.FC<PaymentCheckoutPageProps> = ({
  isOpen, onClose, plan, period, promoCode: initialPromo, onSuccess,
}) => {
  const { t } = useTranslation()
  const { data: cards, refetch: refetchCards } = useUserCards()
  const calculateMutation = useCalculatePayment()
  const subscribeMutation = useSubscribeWithCard()
  const checkoutMutation = useCreateCheckout()
  const validatePromoMutation = useValidatePromo()

  const [tab, setTab] = useState<PaymentTab>('subscribe')
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [showCardPicker, setShowCardPicker] = useState(false)
  const [showAddCard, setShowAddCard] = useState(false)

  // Promo state
  const [promoInput, setPromoInput] = useState(initialPromo ?? '')
  const [promoApplied, setPromoApplied] = useState(false)
  const [promoError, setPromoError] = useState('')
  const [promoType, setPromoType] = useState<string | null>(null)
  const [showPromoInput, setShowPromoInput] = useState(!!initialPromo)

  const [selectedProvider, setSelectedProvider] = useState<'payme' | 'click'>('payme')
  const [calculation, setCalculation] = useState<PaymentCalculation | null>(null)
  const [payError, setPayError] = useState('')

  // Auto-select default card
  useEffect(() => {
    if (cards && cards.length > 0 && !selectedCardId) {
      const defaultCard = cards.find((c) => c.is_default) ?? cards[0]
      setSelectedCardId(defaultCard.id)
    }
  }, [cards, selectedCardId])

  // Calculate payment on mount
  const recalculate = useCallback((appliedPromo?: string): void => {
    if (!plan || !period) return
    calculateMutation.mutate(
      { plan, period, promoCode: appliedPromo },
      {
        onSuccess: (data) => setCalculation(data),
      },
    )
  }, [plan, period, calculateMutation])

  useEffect(() => {
    if (isOpen && plan && period) {
      recalculate(promoApplied ? promoInput.trim() : undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan, period])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setPayError('')
      setShowCardPicker(false)
    }
  }, [isOpen])

  // Handle promo submit
  const handleApplyPromo = (): void => {
    const code = promoInput.trim().toUpperCase()
    if (!code) return

    hapticImpact('light')
    setPromoError('')

    validatePromoMutation.mutate(code, {
      onSuccess: (result) => {
        if (!result.valid) {
          setPromoError(
            result.error === 'cannot_use_own_referral'
              ? t('premium.promoOwnCode')
              : result.error === 'referral_only_new_users'
              ? t('premium.promoOnlyNewUsers')
              : result.error === 'referral_already_used'
              ? t('premium.promoAlreadyUsed')
              : result.error === 'invalid_code'
              ? t('premium.promoInvalid')
              : result.error === 'too_many_attempts'
              ? t('premium.promoTooManyAttempts', { seconds: String(result.retry_after_seconds ?? 600) })
              : String(result.error),
          )
          hapticNotification('error')
          return
        }

        // Referral only applies to monthly
        if (result.type === 'referral' && period !== 'monthly') {
          setPromoError(t('premium.promoMonthlyOnly'))
          hapticNotification('warning')
          return
        }

        setPromoApplied(true)
        setPromoType(result.type ?? null)
        hapticNotification('success')

        // Recalculate with promo
        recalculate(code)
      },
      onError: (err) => {
        setPromoError(err.message)
        hapticNotification('error')
      },
    })
  }

  const handleRemovePromo = (): void => {
    setPromoApplied(false)
    setPromoType(null)
    setPromoInput('')
    setPromoError('')
    recalculate(undefined)
    hapticImpact('light')
  }

  const periodLabel = period === 'monthly' ? t('premium.monthlyTitle') : t('premium.yearlyTitle')

  const handleSubscribe = (): void => {
    if (!selectedCardId) {
      setShowAddCard(true)
      return
    }

    hapticImpact('medium')
    setPayError('')

    subscribeMutation.mutate(
      {
        plan,
        period,
        card_id: selectedCardId,
        promo_code: promoApplied ? promoInput.trim().toUpperCase() : undefined,
      },
      {
        onSuccess: () => {
          hapticNotification('success')
          onSuccess()
        },
        onError: (err) => {
          setPayError(err.message)
          hapticNotification('error')
        },
      },
    )
  }

  const handleOneTimePay = (): void => {
    hapticImpact('medium')
    setPayError('')

    checkoutMutation.mutate(
      {
        plan,
        period,
        provider: selectedProvider,
        promo_code: promoApplied ? promoInput.trim().toUpperCase() : undefined,
      },
      {
        onSuccess: (result) => {
          if (result.type === 'free') {
            hapticNotification('success')
            onSuccess()
            return
          }
          if (result.checkout_url) {
            if (result.payment_id) {
              localStorage.setItem('last_payment_id', result.payment_id)
            }
            window.open(result.checkout_url, '_blank')
          }
        },
        onError: (err) => {
          setPayError(err.message)
          hapticNotification('error')
        },
      },
    )
  }

  const formatPrice = (amount: number): string => amount.toLocaleString('uz-UZ')

  const selectedCard = useMemo((): UserCard | null => {
    if (!cards || !selectedCardId) return null
    return cards.find((c) => c.id === selectedCardId) ?? null
  }, [cards, selectedCardId])

  const cardBrandIcon = (brand: string | null): string => {
    switch (brand) {
      case 'uzcard': return '💳'
      case 'humo': return '🏦'
      case 'visa': return '💎'
      case 'mastercard': return '🔶'
      default: return '💳'
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 inset-0 bg-bg flex flex-col
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[90vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header — safe area for fullscreen */}
            <div className="flex items-center gap-3 px-4 pb-2 shrink-0 tg-header-safe" style={{ paddingTop: 'max(16px, calc(var(--tg-safe-area-inset-top) + 8px))' }}>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04]"
              >
                <ArrowLeft size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">
                {t('premium.paymentDetails')}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {/* Tabs */}
              <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] mt-2">
                {(['subscribe', 'one_time'] as const).map((tabKey) => (
                  <button
                    key={tabKey}
                    onClick={() => { setTab(tabKey); hapticImpact('light') }}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      tab === tabKey
                        ? 'bg-white/[0.08] text-text shadow-sm'
                        : 'text-text-hint hover:text-text'
                    }`}
                  >
                    {tabKey === 'subscribe' ? t('premium.subscribe') : t('premium.oneTimePay')}
                  </button>
                ))}
              </div>

              {/* Plan Info Banner */}
              <div className="mt-4 p-3.5 rounded-xl bg-primary/5 border border-primary/10 flex items-start gap-3">
                <Zap size={18} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text">
                    {tab === 'subscribe'
                      ? t('premium.subscriptionInfo', { period: periodLabel })
                      : t('premium.purchaseInfo', { period: periodLabel })
                    }
                  </p>
                  <p className="text-xs text-text-hint mt-0.5">
                    {tab === 'subscribe' ? t('premium.nextPayDate') : t('premium.validUntil')}
                    {': '}
                    {getNextPayDate(period)}
                  </p>
                </div>
              </div>

              {/* Subscribe Tab: Card Selection */}
              {tab === 'subscribe' && (
                <div className="mt-4">
                  {selectedCard ? (
                    <button
                      onClick={() => setShowCardPicker(!showCardPicker)}
                      className="w-full flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors"
                    >
                      <span className="text-xl">{cardBrandIcon(selectedCard.card_brand)}</span>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-text">{selectedCard.card_number_masked}</p>
                        <p className="text-xs text-text-hint capitalize">{selectedCard.card_brand}</p>
                      </div>
                      <ChevronDown size={16} className={`text-text-hint transition-transform ${showCardPicker ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowAddCard(true)}
                      className="w-full flex items-center gap-3 p-4 rounded-xl border border-dashed border-white/[0.1] hover:bg-white/[0.02] transition-colors"
                    >
                      <Plus size={20} className="text-primary" />
                      <span className="text-sm text-text">{t('premium.addCard')}</span>
                    </button>
                  )}

                  {/* Card picker dropdown */}
                  <AnimatePresence>
                    {showCardPicker && cards && cards.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 space-y-1.5 rounded-xl bg-white/[0.02] border border-white/[0.04] p-2">
                          {cards.map((card) => (
                            <button
                              key={card.id}
                              onClick={() => {
                                setSelectedCardId(card.id)
                                setShowCardPicker(false)
                                hapticImpact('light')
                              }}
                              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                                card.id === selectedCardId
                                  ? 'bg-primary/10 border border-primary/20'
                                  : 'hover:bg-white/[0.04]'
                              }`}
                            >
                              <span className="text-lg">{cardBrandIcon(card.card_brand)}</span>
                              <span className="flex-1 text-sm text-text text-left">{card.card_number_masked}</span>
                              {card.id === selectedCardId && <Check size={16} className="text-primary" />}
                            </button>
                          ))}
                          <button
                            onClick={() => {
                              setShowCardPicker(false)
                              setShowAddCard(true)
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.04] transition-colors"
                          >
                            <Plus size={16} className="text-primary" />
                            <span className="text-sm text-primary">{t('cards.addCard')}</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* One-time Tab: Provider Selection */}
              {tab === 'one_time' && (
                <div className="mt-4">
                  <ProviderSelector
                    selected={selectedProvider}
                    onChange={setSelectedProvider}
                  />
                </div>
              )}

              {/* Promo Code Section */}
              <div className="mt-4">
                {promoApplied ? (
                  /* Applied promo banner */
                  <div className="flex items-center gap-3 p-3.5 rounded-xl bg-success/5 border border-success/15">
                    <Tag size={16} className="text-success shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text">
                        {t('premium.promoApplied')}
                      </p>
                      <p className="text-xs text-success font-mono mt-0.5">
                        {promoInput.toUpperCase()}
                        {promoType === 'referral' && ` (−10%)`}
                        {promoType === 'gift' && ` (${t('giftPremium.status.created')})`}
                      </p>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/[0.04]"
                    >
                      <X size={14} className="text-text-hint" />
                    </button>
                  </div>
                ) : (
                  /* Promo input */
                  <>
                    <button
                      onClick={() => { setShowPromoInput(!showPromoInput); hapticImpact('light') }}
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06]"
                    >
                      <Tag size={16} className="text-text-hint" />
                      <span className="flex-1 text-sm text-text text-left">{t('premium.promoDiscount')}</span>
                      <ChevronRight size={16} className={`text-text-hint transition-transform ${showPromoInput ? 'rotate-90' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {showPromoInput && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="text"
                              value={promoInput}
                              onChange={(e) => {
                                setPromoInput(e.target.value.toUpperCase())
                                setPromoError('')
                              }}
                              placeholder={t('premium.promoPlaceholder')}
                              className="flex-1 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                                text-text text-sm font-mono tracking-wider placeholder:text-text-hint/40
                                focus:outline-none focus:border-primary/30"
                              onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo() }}
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleApplyPromo}
                              isLoading={validatePromoMutation.isPending}
                              disabled={!promoInput.trim()}
                            >
                              <Check size={16} />
                            </Button>
                          </div>
                          {promoError && (
                            <div className="flex items-center gap-1.5 mt-2 text-danger">
                              <AlertCircle size={12} />
                              <p className="text-xs">{promoError}</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>

              {/* Receipt */}
              {calculation && !calculation.error && (
                <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                  <div className="px-4 py-3 space-y-2.5">
                    <ReceiptRow
                      label={t('premium.subscriptionType')}
                      value={periodLabel}
                    />
                    <ReceiptRow
                      label={t('premium.paymentAmount')}
                      value={`${formatPrice(calculation.base_amount)} UZS`}
                    />
                    {calculation.discount_amount > 0 && (
                      <ReceiptRow
                        label={`${t('premium.discount')} (${calculation.discount_percent}%)`}
                        value={`-${formatPrice(calculation.discount_amount)} UZS`}
                        valueClass="text-success"
                      />
                    )}
                    {calculation.referral_balance_used > 0 && (
                      <ReceiptRow
                        label={t('premium.referralBalanceLabel')}
                        value={`-${formatPrice(calculation.referral_balance_used)} UZS`}
                        valueClass="text-success"
                      />
                    )}
                  </div>
                  <div className="border-t border-white/[0.06] px-4 py-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-text">{t('premium.totalPayment')}</span>
                      <span className="text-lg font-display font-bold text-text">
                        {formatPrice(calculation.final_amount)}
                        <span className="text-sm font-normal text-text-hint ml-1">UZS</span>
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}
              {payError && (
                <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-danger/5 border border-danger/15">
                  <AlertCircle size={16} className="text-danger shrink-0" />
                  <p className="text-sm text-danger">{payError}</p>
                </div>
              )}
            </div>

            {/* Bottom Action */}
            <div className="px-5 pt-3 shrink-0 border-t border-white/[0.04]" style={{ paddingBottom: 'max(24px, calc(var(--tg-safe-area-inset-bottom) + 12px))' }}>
              {tab === 'subscribe' ? (
                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleSubscribe}
                  isLoading={subscribeMutation.isPending}
                  disabled={!calculation || subscribeMutation.isPending}
                >
                  {!selectedCardId ? t('premium.addCard') : t('premium.subscribe')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleOneTimePay}
                  isLoading={checkoutMutation.isPending}
                  disabled={!calculation || checkoutMutation.isPending}
                >
                  {t('premium.pay')}
                  {calculation && calculation.final_amount > 0 && (
                    <span className="ml-2 opacity-80">
                      · {formatPrice(calculation.final_amount)} UZS
                    </span>
                  )}
                </Button>
              )}
            </div>
          </motion.div>

          <CardAddSheet
            isOpen={showAddCard}
            onClose={() => {
              setShowAddCard(false)
              void refetchCards()
            }}
          />
        </>
      )}
    </AnimatePresence>
  )
}

// ── Sub-components ──────────────────────────────────────────

const ReceiptRow: React.FC<{
  label: string
  value: string
  valueClass?: string
}> = React.memo(({ label, value, valueClass }) => (
  <div className="flex justify-between text-sm">
    <span className="text-text-hint">{label}</span>
    <span className={valueClass ?? 'text-text'}>{value}</span>
  </div>
))
ReceiptRow.displayName = 'ReceiptRow'

function getNextPayDate(period: string): string {
  const d = new Date()
  if (period === 'yearly') {
    d.setFullYear(d.getFullYear() + 1)
  } else {
    d.setMonth(d.getMonth() + 1)
  }
  return d.toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
